import citiesCSV from './cities.csv?raw';

/**
 * CityRecord represents a city in the game world with demographic,
 * economic, and travel-related attributes.
 */
export interface CityRecord {
  /** Unique numeric identifier for the city */
  id: number;
  /** Airport code (e.g., "JFK", "LHR") */
  code: string;
  /** ISO 3166-1 alpha-2 country code */
  iso2: string;
  /** Bloc or regional grouping */
  bloc: string;
  /** Latitude coordinate */
  lat: number;
  /** Longitude coordinate */
  lon: number;
  /** City population count */
  population: number;
  /** Median income in the city */
  income_median: number;
  /** Gini coefficient for income inequality (0-1) */
  income_gini: number;
  /** Attractiveness score for business travelers */
  score_business: number;
  /** Attractiveness score for culture/tourism travelers */
  score_culture: number;
  /** Attractiveness score for nature/outdoor travelers */
  score_nature: number;
  /** Attractiveness score for family travelers */
  score_family: number;
  /** Initial cost of jet fuel at this location */
  initial_cost_jetfuel: number;
  /** Henley openness index (visa-free access) */
  henley_openness: number;
  /** Henley passport power ranking */
  henley_passport: number;
  /** Desirability score for airport construction (1-5) */
  desirability_score: number;
  /** Landing fee in dollars per metric ton (1000 kg) */
  landing_fee: number;
}

/**
 * Parse CSV data into city records
 */
function parseCSV(csvText: string): CityRecord[] {
  const lines = csvText.split(/\r?\n/).filter(line => line.trim());
  if (lines.length === 0) return [];
  
  // Parse header
  const header = parseCSVLine(lines[0]).map(h => h.trim());
  const colMap: Record<string, number> = {};
  header.forEach((col, i) => {
    colMap[col] = i;
  });
  
  const cities: CityRecord[] = [];
  
  // Parse each data row
  for (let i = 1; i < lines.length; i++) {
    // Handle CSV with quoted fields that may contain commas
    const record = parseCSVLine(lines[i]);
    
    const city: CityRecord = {
      id: parseInt(record[colMap['id']] || '0', 10),
      code: record[colMap['code']] || '',
      iso2: record[colMap['iso2']] || '',
      bloc: '', // Not in CSV, set default
      lat: parseFloat(record[colMap['lat']] || '0'),
      lon: parseFloat(record[colMap['lon']] || '0'),
      population: parseInt((record[colMap['pop']] || '0').replace(/,/g, ''), 10),
      income_median: parseFloat((record[colMap['income_median']] || '0').replace(/[$,]/g, '')),
      income_gini: parseFloat(record[colMap['income_gini']] || '0'),
      score_business: parseFloat(record[colMap['score_business']] || '0'),
      score_culture: parseFloat(record[colMap['score_culture']] || '0'),
      score_nature: parseFloat(record[colMap['score_nature']] || '0'),
      score_family: parseFloat(record[colMap['score_family']] || '0'),
      initial_cost_jetfuel: parseFloat((record[colMap['initial_cost_jetfuel']] || '0').replace('$', '')),
      henley_openness: 0.5, // Default value, not in CSV
      henley_passport: 0.5, // Default value, not in CSV
      desirability_score: parseInt(record[colMap['desirability_score']] || '1', 10),
      landing_fee: parseFloat(record[colMap['landing_fee']] || '5.0'),
    };
    
    cities.push(city);
  }
  
  return cities;
}

/**
 * Parse a single CSV line, handling quoted fields
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      // RFC 4180 escaped quote inside a quoted field.
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  
  result.push(current.trim());
  return result;
}

const cityData = parseCSV(citiesCSV);

/**
 * Optimized city data access with O(1) lookups.
 * This class provides fast indexed access to city records.
 */
class CityDatabase {
  private byId: Map<number, CityRecord>;
  private byCode: Map<string, CityRecord>;
  private data: CityRecord[];

  constructor(cities: CityRecord[]) {
    this.data = cities;
    this.byId = new Map(cities.map(city => [city.id, city]));
    this.byCode = new Map(cities.map(city => [city.code.toLowerCase(), city]));
  }

  /** Get all cities as an array */
  getAll(): CityRecord[] {
    return this.data;
  }

  /** Get city by ID (O(1) lookup) */
  getById(id: number): CityRecord | undefined {
    return this.byId.get(id);
  }

  /** Get city by airport code (O(1) lookup, case-insensitive) */
  getByCode(code: string): CityRecord | undefined {
    return this.byCode.get(code.toLowerCase());
  }

  /** Get cities by bloc (O(n) operation) */
  getByBloc(bloc: string): CityRecord[] {
    return this.data.filter(city => city.bloc === bloc);
  }

  /** Get cities by country ISO2 code (O(n) operation) */
  getByCountry(iso2: string): CityRecord[] {
    return this.data.filter(city => city.iso2 === iso2);
  }

  /** Get total number of cities */
  get count(): number {
    return this.data.length;
  }
}

/**
 * Singleton city database instance with all city data loaded from cities.csv.
 * Provides optimized O(1) lookups by ID and airport code.
 * 
 * @example
 * ```typescript
 * import { cities } from './data/cities';
 * 
 * const tokyo = cities.getById(183);
 * const london = cities.getByCode('LHR');
 * const usCities = cities.getByCountry('US');
 * ```
 */
export const cities = new CityDatabase(cityData);

// ─── Localized city name/summary helpers ────────────────────────────────────────

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/config';

/** A city's airport code from its id — how the panels name a place in a line — or the id while it has none. */
export function cityCode(cityId: number): string {
  return cities.getById(cityId)?.code ?? String(cityId);
}

/** A route by its two airports' codes, as a flight names its route in a line: "HNL–ANC". */
export function routeCodes(originId: number, destId: number): string {
  return `${cityCode(originId)}–${cityCode(destId)}`;
}

/**
 * A city's name in the player's language, from its airport code — for code
 * outside React: a toast, a domain handler. The code itself while it has no
 * name. In a component, `useCityTranslation().getCityName`, which follows a
 * change of language.
 */
export function cityName(code: string, fallback?: string): string {
  return i18n.t(`${code}.name`, { ns: 'cities', defaultValue: fallback ?? code });
}

/**
 * React hook that returns helpers for looking up localized city names and summaries.
 * Uses the `cities` i18n namespace with English values as fallbacks.
 *
 * @example
 * ```tsx
 * const { getCityName, getCitySummary } = useCityTranslation();
 * return <span>{getCityName('LHR')}</span>;
 * ```
 */
export function useCityTranslation() {
  const { t } = useTranslation('cities');
  const getCityName = useCallback(
    (code: string, fallback?: string) => t(`${code}.name`, { defaultValue: fallback ?? code }),
    [t]
  );
  const getCitySummary = useCallback(
    (code: string, fallback?: string) => t(`${code}.summary`, { defaultValue: fallback ?? '' }),
    [t]
  );

  return {
    /** Get the localized city name for an airport code, falling back to the code itself. */
    getCityName,
    /** Get the localized city summary/description for an airport code. */
    getCitySummary,
  };
}
